import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Auto-growing by default (task: no internal scrollbar on any multi-line
 * field, 2026-09-24). Every Textarea in the app grows to fit its own
 * content instead of scrolling inside a fixed-height box: this is the one
 * shared primitive nearly every multi-line field in the app already
 * renders through, so building it in here is what makes the fix apply
 * everywhere at once, first proven as a one-off, hand-rolled ref+effect on
 * Shows.jsx's Meeting Topics Hint field (2026-09-22), now the real
 * implementation instead of that copy.
 *
 * A caller's own `rows`, `min-h-*` or fixed `h-*` className still sets the
 * floor height exactly as before (nothing here removes those): resize()
 * below always resets to `height: auto` first, which settles back to
 * whatever that floor is, then grows from there via scrollHeight. Nothing
 * ever shrinks a field below its own declared minimum, only above it.
 *
 * `resize-none overflow-hidden` are baked into the base classes rather than
 * left for each caller to add, since an internal scrollbar or a manual
 * drag-resize handle are exactly the two things this exists to remove.
 *
 * Resizes via useLayoutEffect, not useEffect, keyed on `value`: this app is
 * a plain client-rendered SPA (no SSR) so there is no server-side warning
 * to worry about, and useLayoutEffect runs before the browser paints, so a
 * field that opens already full of text (an email body, a signature) is
 * sized correctly from its very first paint rather than flashing at the
 * wrong height for a frame first. Firing on every value change covers
 * typing, a template's "Reset to default" snapping in a new value, and an
 * async load landing after mount, not just the keystrokes onChange itself
 * would catch.
 */
const Textarea = React.forwardRef(({ className, value, onChange, ...props }, forwardedRef) => {
  const innerRef = React.useRef(null)

  const setRefs = React.useCallback(
    (node) => {
      innerRef.current = node
      if (typeof forwardedRef === "function") forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    },
    [forwardedRef]
  )

  const resize = React.useCallback(() => {
    const el = innerRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }, [])

  React.useLayoutEffect(() => {
    resize()
  }, [value, resize])

  return (
    (<textarea
      ref={setRefs}
      value={value}
      onChange={(e) => {
        resize()
        onChange?.(e)
      }}
      className={cn(
        "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none overflow-hidden",
        className
      )}
      {...props} />)
  );
})
Textarea.displayName = "Textarea"

export { Textarea }
